import React, {useState, useEffect, useMemo, useCallback, useRef} from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import Slider from '@mui/material/Slider';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Autocomplete from '@mui/material/Autocomplete';
import {bankMatchesSearch} from '../../utils/bankSearch';
import TextField from '@mui/material/TextField';
import CopyButton from '../CopyButton/CopyButton';
import HelpButton from "../HelpButton/HelpButton";
import {formatApiErrors, validateRequisiteForm} from '../../utils/requisiteValidation';
import {downloadTotalNotifyApk} from '../../utils/downloadApk';
import './RequisiteFormModal.scss';

Modal.setAppElement('#root');

// Custom dropdown component
function CustomSelect({ value, onChange, options, placeholder = 'Select...' }) {
    const [open, setOpen] = useState(false);
    const ref = React.useRef(null);

    React.useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const selectedLabel = options.find(o => o.value === value)?.label || placeholder;

    return (
        <div className={`custom-select ${open ? 'open' : ''}`} ref={ref}>
            <button type="button" className="custom-select__trigger" onClick={() => setOpen(!open)}>
                <span>{selectedLabel}</span>
                <svg width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 1L5 5L9 1"/>
                </svg>
            </button>
            {open && (
                <div className="custom-select__dropdown">
                    {options.map((opt) => (
                        <button
                            type="button"
                            key={opt.value}
                            className={`custom-select__option ${value === opt.value ? 'selected' : ''}`}
                            onClick={() => { onChange(opt.value); setOpen(false); }}
                        >
                            {value === opt.value && <span className="custom-select__check">✓</span>}
                            {opt.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// STATUS_OPTIONS and FIELD_NAMES moved inside component for i18n

const DEFAULT_FORM_DATA = {
    credentials: '',
    credentials_limit: '',
    status: '1',
    comment: '',
    can_autoclose: false,
    kind: '',
    payment_type: '',
    min_rur: '0',
    max_rur: '100000',
    min_user_orders: 0,
    max_requests_in_range: 0,
    range_in_minutes: 60,
    max_success_requests_per_day: 0,
    reservation_range_percent: 0,
    device_id: '',
    last4digits: '',
    iban_last4digits: '',
    recipient: '',
};

function extractAccounts(clientData) {
    const accounts = clientData.accounts || [];
    const jars = clientData.jars || [];
    if (accounts.length === 0) return [];

    const out = [];
    for (const acc of accounts) {
        out.push({
            id: acc.id,
            type: acc.type,
            balance: acc.balance,
            title: acc.maskedPan && acc.maskedPan[0] ? acc.maskedPan[0] : acc.type,
        });
    }
    for (const jar of jars) {
        out.push({
            id: jar.id,
            type: 'jars',
            balance: jar.balance,
            title: jar.title,
        });
    }
    return out;
}

export default function RequisiteFormModal({
    isOpen,
    onClose,
    editData,
    onSuccess,
    apiUrl,
    availablePayments,
    userCanSetMinOrders,
    theme,
    novodroidToken,
    novodroidUrl,
    bankKinds,
    bankCurrencies,
    defaultBankCurrency,
    banksWithoutCard,
    banksWithIban,
}) {
    const { t } = useTranslation('forms');

    const STATUS_OPTIONS = [
        ['1', t('forms:requisiteForm.statusOptions.1')], ['2', t('forms:requisiteForm.statusOptions.2')], ['7', t('forms:requisiteForm.statusOptions.7')],
        ['34', t('forms:requisiteForm.statusOptions.34')], ['4', t('forms:requisiteForm.statusOptions.4')],
        ['5', t('forms:requisiteForm.statusOptions.5')], ['6', t('forms:requisiteForm.statusOptions.6')], ['8', t('forms:requisiteForm.statusOptions.8')], ['9', t('forms:requisiteForm.statusOptions.9')]
    ];

    const FIELD_NAMES = {
        credentials: t('forms:requisiteForm.credentials'),
        credentials_limit: t('forms:requisiteForm.limit'),
        min_rur: t('forms:requisiteForm.minAmount'),
        max_rur: t('forms:requisiteForm.maxAmount'),
        max_success_requests_per_day: t('forms:requisiteForm.completedPerDay'),
        max_requests_in_range: t('forms:requisiteForm.maxRequestsInRange'),
        payment_type: t('forms:requisiteForm.paymentMethod'),
        status: t('forms:requisiteForm.status'),
        recipient: t('forms:requisiteForm.recipient'),
        device_id: t('forms:requisiteForm.deviceCode'),
        last4digits: t('forms:requisiteForm.last4digits'),
        iban_last4digits: t('forms:requisiteForm.ibanLast4digits'),
        __all__: t('common:error')
    };

    const [formData, setFormData] = useState({...DEFAULT_FORM_DATA});
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(false);
    const [errorText, setErrorText] = useState('');
    const [tokenMonobankStatus, setTokenMonobankStatus] = useState(true);
    const [tokenMonobankAccounts, setTokenMonobankAccounts] = useState([]);
    const [choosenAccount, setChoosenAccount] = useState('');
    const [showBankWarning, setShowBankWarning] = useState(false);
    const [bankWarningMessage, setBankWarningMessage] = useState('');
    const [showNovodroidModal, setShowNovodroidModal] = useState(false);
    const [apkAvailable, setApkAvailable] = useState(false);

    const filteredBankKinds = useMemo(() => {
        if (!bankKinds || !formData.payment_type) return bankKinds;
        const payment = availablePayments.find(p => p[1] === formData.payment_type);
        if (!payment || !payment[3]) return bankKinds;
        const currencyCode = payment[3];
        return bankKinds.filter(([bankName]) => {
            if (bankName === '' || bankName === 'any_bank') return true;
            const currencies = (bankCurrencies || {})[bankName];
            if (!currencies) return currencyCode === (defaultBankCurrency || 'RUB');
            return currencies.includes(currencyCode);
        });
    }, [bankKinds, formData.payment_type, availablePayments, bankCurrencies, defaultBankCurrency]);

    const isSbp = useMemo(() => {
        if (!formData.payment_type || !availablePayments) return false;
        const payment = availablePayments.find(p => p[1] === formData.payment_type);
        return payment && payment[4] ? payment[4].includes('sbp') : false;
    }, [formData.payment_type, availablePayments]);

    // Проверка обязательности last4digits при автозакрытии
    const isLast4digitsRequired = formData.can_autoclose
        && formData.kind
        && formData.kind !== 'any_bank'
        && !(banksWithoutCard || []).includes(formData.kind);

    // Проверка обязательности iban_last4digits (ПУМБ и банки с Рахунок)
    const isIbanRequired = formData.can_autoclose
        && formData.kind
        && (banksWithIban || []).includes(formData.kind);

    const jwtToken = localStorage.getItem('accessToken');
    const isEditing = editData !== null;

    const updateField = useCallback((field, value) => {
        setFormData(prev => ({...prev, [field]: value}));
    }, []);

    const checkMonoBankToken = useCallback((token, preselectedAccount) => {
        updateField('device_id', token);
        if (token.length > 40) {
            const get_accounts = async () => {
                try {
                    const response = await fetch('https://api.monobank.ua/personal/client-info', {
                        headers: {'X-Token': token}
                    });
                    if (!response.ok) {
                        setTokenMonobankStatus(false);
                        setTokenMonobankAccounts([]);
                        return;
                    }
                    setTokenMonobankStatus(true);
                    const accounts = extractAccounts(await response.json());
                    setTokenMonobankAccounts(accounts);
                    setChoosenAccount(current => preselectedAccount || current || accounts[0]?.id || '');
                } catch (error) {
                    setTokenMonobankStatus(false);
                    setTokenMonobankAccounts([]);
                }
            };
            get_accounts();
        } else {
            setTokenMonobankStatus(false);
        }
    }, [updateField]);

    // A new array from header polling must not reset an in-progress form.
    const defaultPayment = availablePayments?.[0]?.[1] ?? '';
    const initializedForm = useRef(null);

    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        fetch(apiUrl + 'apk/status/', {
            headers: {'Authorization': `JWT ${jwtToken}`}
        })
        .then(r => r.json())
        .then(data => { if (!cancelled) setApkAvailable(data.available); })
        .catch(() => {});
        return () => { cancelled = true; };
    }, [isOpen, apiUrl, jwtToken]);

    useEffect(() => {
        if (!isOpen) {
            initializedForm.current = null;
            return;
        }
        // Initialize each opened record once, even if payment defaults change while typing.
        if (initializedForm.current && initializedForm.current.editData === editData) return;
        initializedForm.current = { editData };

        if (editData) {
            const monoDeviceId = editData.device_id.value;
            let deviceId = monoDeviceId;
            let accountId = '';

            if (editData.payment_type_id.value === 4 && monoDeviceId.indexOf('|') >= 0) {
                const chunks = monoDeviceId.split('|');
                deviceId = chunks[0];
                accountId = chunks[1];
            }

            setFormData({
                credentials: editData.credentials.value,
                credentials_limit: editData.credentials_limit.value,
                status: editData.status.value,
                comment: editData.comment.value,
                can_autoclose: editData.can_autoclose.value,
                kind: editData.kind?.value || '',
                payment_type: editData.payment_type_id.value,
                min_rur: editData.min_rur.value,
                max_rur: editData.max_rur.value,
                min_user_orders: editData.min_user_orders?.value || 0,
                max_requests_in_range: editData.max_requests_in_range.value,
                range_in_minutes: editData.range_in_minutes.value,
                max_success_requests_per_day: editData.max_success_requests_per_day.value,
                reservation_range_percent: editData.reservation_range_percent.value,
                device_id: deviceId,
                last4digits: editData.last4digits.value,
                iban_last4digits: editData.iban_last4digits ? editData.iban_last4digits.value : '',
                recipient: editData.recipient.value,
            });

            setChoosenAccount(accountId);

            if (editData.payment_type_id.value === 4 && accountId) {
                checkMonoBankToken(deviceId, accountId);
            } else {
                setTokenMonobankAccounts([]);
                setTokenMonobankStatus(true);
            }
        } else {
            // Create mode defaults
            setFormData({...DEFAULT_FORM_DATA, payment_type: defaultPayment});
            setChoosenAccount('');
            setTokenMonobankAccounts([]);
            setTokenMonobankStatus(true);
        }

        setError(false);
        setErrorText('');
        setSubmitting(false);
        setShowNovodroidModal(false);
    }, [isOpen, editData, defaultPayment, checkMonoBankToken]);

    const handleSubmit = async () => {
        setSubmitting(true);
        setError(false);

        const validationError = validateRequisiteForm({
            formData,
            availablePayments: availablePayments || [],
            isSbp,
            isLast4digitsRequired,
            isIbanRequired,
        });
        if (validationError) {
            setError(true);
            setErrorText(t(`forms:requisiteForm.validation.${validationError}`));
            setSubmitting(false);
            return;
        }

        const data = {
            credentials: formData.credentials,
            credentials_limit: parseInt(formData.credentials_limit, 10),
            status: formData.status,
            comment: formData.comment,
            can_autoclose: formData.can_autoclose,
            kind: formData.kind,
            payment_type: formData.payment_type,
            min_rur: parseInt(formData.min_rur, 10),
            max_rur: parseInt(formData.max_rur, 10),
            min_user_orders: parseInt(formData.min_user_orders, 10) || 0,
            max_requests_in_range: formData.max_requests_in_range,
            range_in_minutes: formData.range_in_minutes,
            max_success_requests_per_day: formData.max_success_requests_per_day,
            reservation_range_percent: formData.reservation_range_percent,
            device_id: formData.device_id,
            last4digits: formData.last4digits,
            iban_last4digits: formData.iban_last4digits,
            recipient: formData.recipient,
        };

        if (data.payment_type === 4 && choosenAccount) {
            data.device_id = `${formData.device_id}|${choosenAccount}`;
        }

        const url = isEditing
            ? apiUrl + `exchange/managercredentials/${editData.pk}/`
            : apiUrl + 'exchange/managercredentials/';
        const method = isEditing ? 'PUT' : 'POST';

        try {
            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `JWT ${jwtToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });

            if (response.ok) {
                const responseData = await response.json();
                if (responseData.warnings && responseData.warnings.length > 0) {
                    setBankWarningMessage(responseData.warnings[0].message);
                    setShowBankWarning(true);
                    setSubmitting(false);
                } else {
                    onSuccess();
                }
            } else if (response.status === 401) {
                localStorage.clear();
                window.location.href = '/auth';
            } else {
                let errorData = null;
                try {
                    errorData = await response.json();
                } catch (parseError) {
                    // Non-JSON upstream errors fall back to the localized text.
                }
                const formattedErrors = formatApiErrors(
                    errorData,
                    FIELD_NAMES,
                    t('common:unknownError'),
                );
                setError(true);
                setErrorText(formattedErrors);
            }
        } catch (err) {
            setError(true);
            setErrorText(t('forms:requisiteForm.serverError'));
        } finally {
            setSubmitting(false);
        }
    };

    const renderNovodroidInstructions = () => (
        <div className="novodroid-instructions">
            <div className="requisite-form-header">
                <h2 className="requisite-form-title">{t('forms:novodroid.title')}</h2>
                <button className="requisite-form-close" onClick={() => setShowNovodroidModal(false)}>×</button>
            </div>

            <div className="requisite-form-body">
                {novodroidToken ? (
                    <div className="novodroid-endpoint">
                        <label>Endpoint URL:</label>
                        <div className="novodroid-url">
                            {novodroidUrl}
                        </div>
                        <CopyButton text={novodroidUrl || ''} />
                    </div>
                ) : (
                    <div className="confirmation-modal-warning">
                        {t('forms:novodroid.tokenNotGenerated')}
                    </div>
                )}

                {apkAvailable ? (
                    <button
                        className="download-apk-btn"
                        onClick={async () => {
                            try {
                                await downloadTotalNotifyApk(apiUrl, jwtToken);
                            } catch (error) {
                                setApkAvailable(false);
                            }
                        }}
                    >
                        {t('forms:novodroid.downloadApp')}
                    </button>
                ) : (
                    <button disabled className="download-apk-btn disabled">
                        {t('forms:novodroid.appUnavailable')}
                    </button>
                )}

                <div className="novodroid-steps">
                    <label>{t('forms:novodroid.stepsTitle')}</label>
                    <ol>
                        <li>{t('forms:novodroid.step1')}</li>
                        <li>{t('forms:novodroid.step2')}</li>
                        <li>{t('forms:novodroid.step3')}</li>
                        <li>{t('forms:novodroid.step4')}</li>
                        <li>{t('forms:novodroid.step5')}</li>
                        <li>{t('forms:novodroid.step6')}</li>
                    </ol>
                </div>
            </div>
        </div>
    );

    return (
        <>
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            className={`requisite-form-modal ${theme === 'dark' ? 'dark' : ''}`}
            overlayClassName="requisite-form-modal-overlay"
            contentLabel={isEditing ? t('forms:requisiteForm.titleEdit') : t('forms:requisiteForm.titleCreate')}
        >
            {showNovodroidModal ? renderNovodroidInstructions() : (
                <>
                    {/* Header */}
                    <div className="requisite-form-header">
                        <h2 className="requisite-form-title">
                            {isEditing ? t('forms:requisiteForm.titleEdit') : t('forms:requisiteForm.titleCreate')}
                        </h2>
                        <button className="requisite-form-close" onClick={onClose}>×</button>
                    </div>

                    <div className="requisite-form-body">
                        {error && (
                            <div className="requisite-form-error">
                                {errorText.split('\n').map((line, i) => (
                                    <div key={i}>{line}</div>
                                ))}
                            </div>
                        )}

                        {/* Реквизиты */}
                        <div className="requisite-form-row">
                            <label className={!isEditing && formData.credentials === '' ? 'required' : ''}>
                                {t('forms:requisiteForm.credentials')}
                            </label>
                            <input
                                type="text"
                                placeholder={t('forms:requisiteForm.credentials')}
                                value={formData.credentials}
                                readOnly={isEditing}
                                onChange={(e) => updateField('credentials', e.target.value)}
                            />
                        </div>

                        {/* Последние 4 цифры */}
                        <div className="requisite-form-row">
                            <label className={isLast4digitsRequired && !formData.last4digits ? 'required' : ''}>{t('forms:requisiteForm.last4digits')}</label>
                            <input
                                type="text"
                                value={formData.last4digits}
                                onChange={(e) => updateField('last4digits', e.target.value)}
                            />
                        </div>

                        {/* Последние 4 цифры IBAN (рахунку) — только для банков из banksWithIban */}
                        {isIbanRequired && (
                            <div className="requisite-form-row">
                                <label className={isIbanRequired && !formData.iban_last4digits ? 'required' : ''}>
                                    {t('forms:requisiteForm.ibanLast4digits')}
                                </label>
                                <div style={{fontSize: '12px', opacity: 0.7, marginBottom: '4px'}}>
                                    {t('forms:requisiteForm.ibanLast4digitsHelp')}
                                </div>
                                <input
                                    type="text"
                                    maxLength={4}
                                    value={formData.iban_last4digits}
                                    onChange={(e) => updateField('iban_last4digits', e.target.value.replace(/\D/g, ''))}
                                />
                            </div>
                        )}

                        {/* Получатель */}
                        <div className="requisite-form-row">
                            <label>{t('forms:requisiteForm.recipient')}</label>
                            <input
                                type="text"
                                value={formData.recipient}
                                onChange={(e) => updateField('recipient', e.target.value)}
                            />
                        </div>

                        {/* Лимит */}
                        <div className="requisite-form-row">
                            <label className={formData.credentials_limit === '' ? 'required' : ''}>{t('forms:requisiteForm.limit')}</label>
                            <input
                                type="text"
                                placeholder={t('forms:requisiteForm.limitPlaceholder')}
                                value={formData.credentials_limit}
                                onChange={(e) => {
                                    if (e.target.value.length <= 8 && e.target.value <= 50000000) {
                                        updateField('credentials_limit', e.target.value.replace(/\D/, ''));
                                    }
                                }}
                            />
                        </div>

                        {/* Статус — dropdown */}
                        <div className="requisite-form-row">
                            <label>{t('forms:requisiteForm.status')}</label>
                            <CustomSelect
                                value={formData.status}
                                onChange={(val) => updateField('status', val)}
                                options={STATUS_OPTIONS.map(s => ({ value: s[0], label: s[1] }))}
                                placeholder={t('forms:requisiteForm.statusPlaceholder')}
                            />
                        </div>

                        {/* Подсказка */}
                        <div className="requisite-form-row">
                            <label>{t('forms:requisiteForm.hint')}</label>
                            <input
                                type="text"
                                placeholder={t('forms:requisiteForm.hint')}
                                value={formData.comment}
                                onChange={(e) => updateField('comment', e.target.value)}
                            />
                        </div>

                        {/* Диапазон резервирования */}
                        <div className="requisite-form-row">
                            <label style={{display: "flex", alignItems: "center", gap: "6px"}}>
                                <HelpButton
                                    info_text={t('forms:requisiteForm.reservationRangeHelp')}
                                />
                                {t('forms:requisiteForm.reservationRange')}
                            </label>
                            <input
                                type="text"
                                placeholder={t('forms:requisiteForm.noLimitPlaceholder')}
                                value={formData.reservation_range_percent}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, '');
                                    const numericValue = value !== '' ? parseInt(value, 10) : '';
                                    if (numericValue === '' || (numericValue >= 0 && numericValue <= 100)) {
                                        updateField('reservation_range_percent', value);
                                    }
                                }}
                            />
                        </div>

                        {/* Выполненных в сутки */}
                        <div className="requisite-form-row">
                            <label>{t('forms:requisiteForm.completedPerDay')}</label>
                            <input
                                type="text"
                                placeholder={t('forms:requisiteForm.noLimitPlaceholder')}
                                value={formData.max_success_requests_per_day}
                                onChange={(e) => {
                                    if (e.target.value.length <= 22) {
                                        updateField('max_success_requests_per_day', e.target.value.replace(/\D/, ''));
                                    }
                                }}
                            />
                        </div>

                        {/* Макс заявок в диапазон */}
                        <div className="requisite-form-row">
                            <label>{t('forms:requisiteForm.maxRequestsInRange')}</label>
                            <input
                                type="text"
                                placeholder={t('forms:requisiteForm.noLimitPlaceholder')}
                                value={formData.max_requests_in_range}
                                onChange={(e) => updateField('max_requests_in_range', e.target.value.replace(/\D/, ''))}
                            />
                        </div>

                        {/* Slider диапазон */}
                        <div className="requisite-form-row" style={{marginBottom: '36px'}}>
                            <label>{t('forms:requisiteForm.rangeInMinutes')}</label>
                        </div>
                        <Slider
                            value={formData.range_in_minutes}
                            onChange={(e) => updateField('range_in_minutes', e.target.value)}
                            valueLabelDisplay="on"
                            marks={[{value: 1, label: '1'}, {value: 60, label: '60'}]}
                            min={1}
                            max={60}
                            step={1}
                            sx={{
                                width: '100%',
                                color: 'var(--accent, #2787F5)',
                                '& .MuiSlider-markLabel': {
                                    color: 'var(--text-secondary)',
                                },
                            }}
                        />

                        {/* Мин сумма */}
                        <div className="requisite-form-row">
                            <label className={formData.min_rur === '' ? 'required' : ''}>{t('forms:requisiteForm.minAmount')}</label>
                            <input
                                type="text"
                                placeholder={t('forms:requisiteForm.minAmountPlaceholder')}
                                value={formData.min_rur}
                                onChange={(e) => {
                                    if (e.target.value.length <= 6 && e.target.value <= 100000) {
                                        updateField('min_rur', e.target.value.replace(/\D/, ''));
                                    }
                                }}
                            />
                        </div>

                        {/* Макс сумма */}
                        <div className="requisite-form-row">
                            <label className={formData.max_rur === '' ? 'required' : ''}>{t('forms:requisiteForm.maxAmount')}</label>
                            <input
                                type="text"
                                placeholder={t('forms:requisiteForm.maxAmountPlaceholder')}
                                value={formData.max_rur}
                                onChange={(e) => {
                                    if (e.target.value.length <= 6 && e.target.value <= 100000) {
                                        updateField('max_rur', e.target.value.replace(/\D/, ''));
                                    }
                                }}
                            />
                        </div>

                        {/* Мин заявок клиента */}
                        {userCanSetMinOrders === 'yes' && (
                            <div className="requisite-form-row">
                                <label style={{display: "flex", alignItems: "center", gap: "6px"}}>
                                    <HelpButton
                                        info_text={t('forms:requisiteForm.minClientOrdersHelp')}
                                    />
                                    {t('forms:requisiteForm.minClientOrders')}
                                </label>
                                <input
                                    type="text"
                                    placeholder="0"
                                    value={formData.min_user_orders}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, '');
                                        updateField('min_user_orders', value);
                                    }}
                                />
                            </div>
                        )}

                        {/* Метод оплаты — dropdown */}
                        <div className="requisite-form-row">
                            <label>{t('forms:requisiteForm.paymentMethod')}</label>
                            <CustomSelect
                                value={formData.payment_type}
                                onChange={(val) => {
                                    updateField('payment_type', val);
                                    // Сброс банка при смене способа оплаты (валюта может измениться)
                                    updateField('kind', '');
                                }}
                                options={availablePayments.map(b => ({ value: b[1], label: `${b[0]} +${b[2]}%` }))}
                                placeholder={t('forms:requisiteForm.paymentMethodPlaceholder')}
                            />
                        </div>

                        {/* Bank kind selector — всегда видим, обязателен для СБП */}
                        {filteredBankKinds && filteredBankKinds.length > 0 && (
                            <div className="requisite-form-row">
                                <label className={isSbp && !formData.kind ? 'required' : ''}>
                                    {isSbp ? t('forms:requisiteForm.bank') : t('forms:requisiteForm.bankOptional')}
                                </label>
                                <Autocomplete
                                    size="small"
                                    fullWidth
                                    options={filteredBankKinds}
                                    getOptionLabel={(option) => option[1] || ''}
                                    value={filteredBankKinds.find(b => b[0] === formData.kind && b[0] !== '') || null}
                                    onChange={(e, newValue) => updateField('kind', newValue ? newValue[0] : '')}
                                    filterOptions={(options, {inputValue}) => {
                                        return options.filter((option) => bankMatchesSearch(option, inputValue));
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder={t('forms:requisiteForm.bankPlaceholder')}
                                            sx={{
                                                '& .MuiInputBase-root': {
                                                    color: 'var(--text-primary)',
                                                    backgroundColor: 'var(--bg-secondary)',
                                                    borderRadius: '8px',
                                                },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'var(--border-color)',
                                                },
                                                '& .MuiInputBase-input::placeholder': {
                                                    color: 'var(--text-secondary)',
                                                    opacity: 1,
                                                },
                                            }}
                                        />
                                    )}
                                    noOptionsText={t('forms:requisiteForm.bankNotFound')}
                                    clearText={t('forms:requisiteForm.clear')}
                                    slotProps={{
                                        popper: {
                                            sx: {
                                                zIndex: 10001,
                                            },
                                        },
                                        paper: {
                                            sx: {
                                                backgroundColor: 'var(--bg-primary)',
                                                color: 'var(--text-primary)',
                                                border: '1px solid var(--border-color)',
                                                borderRadius: '10px',
                                                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                                mt: '4px',
                                            },
                                        },
                                        listbox: {
                                            sx: {
                                                '& .MuiAutocomplete-option': {
                                                    color: 'var(--text-primary)',
                                                    fontSize: '14px',
                                                    borderRadius: '6px',
                                                    mx: '4px',
                                                    '&:hover': {
                                                        backgroundColor: 'var(--bg-secondary)',
                                                    },
                                                    '&[aria-selected="true"]': {
                                                        backgroundColor: 'var(--bg-secondary)',
                                                        color: 'var(--accent)',
                                                        fontWeight: 500,
                                                    },
                                                },
                                            },
                                        },
                                    }}
                                    sx={{
                                        '& .MuiSvgIcon-root': {
                                            color: 'var(--text-secondary)',
                                        },
                                    }}
                                />
                            </div>
                        )}

                        {/* Автозакрытие */}
                        <div className="requisite-form-row">
                            <label>{t('forms:requisiteForm.autoclose')}</label>
                            <div className="requisite-form-toggle-row">
                                <button
                                    onClick={() => updateField('can_autoclose', true)}
                                    className={`toggle-btn ${formData.can_autoclose ? 'selected' : ''}`}
                                >
                                    {t('common:yes')}
                                </button>
                                <button
                                    onClick={() => updateField('can_autoclose', false)}
                                    className={`toggle-btn ${!formData.can_autoclose ? 'selected' : ''}`}
                                >
                                    {t('common:no')}
                                </button>
                                {formData.can_autoclose && (
                                    <button
                                        onClick={() => setShowNovodroidModal(true)}
                                        className="novodroid-help-btn"
                                        title={t('forms:novodroid.title')}
                                    >
                                        ?
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Device ID / MonoBank */}
                        {formData.can_autoclose && (
                            <>
                                <div className="requisite-form-row">
                                    {formData.payment_type === 4 ? (
                                        <>
                                            <label style={{display: "flex", alignItems: "center", gap: "6px"}}>
                                                <HelpButton
                                                    info_text={t('forms:requisiteForm.monobankTokenHelp')}
                                                />
                                                <span className={tokenMonobankStatus === false ? 'required' : ''}>
                                                    {t('forms:requisiteForm.monobankToken')}
                                                </span>
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.device_id}
                                                onChange={(e) => checkMonoBankToken(e.target.value)}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <label className={formData.device_id === '' ? 'required' : ''}>
                                                {t('forms:requisiteForm.deviceCode')}
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.device_id}
                                                onChange={(e) => updateField('device_id', e.target.value)}
                                            />
                                        </>
                                    )}
                                </div>
                                {formData.payment_type === 4 && tokenMonobankStatus && tokenMonobankAccounts.length > 0 && (
                                    <div className="requisite-form-row">
                                        <label>{t('forms:requisiteForm.selectAccount')}</label>
                                        <FormControl sx={{width: "100%"}} size="small">
                                            <Select
                                                id="account-select"
                                                value={choosenAccount}
                                                onChange={(e) => {
                                                    if (e.target.value.length > 0) {
                                                        setChoosenAccount(e.target.value);
                                                    }
                                                }}
                                                displayEmpty
                                                inputProps={{'aria-label': 'Without label'}}
                                                MenuProps={{
                                                    sx: { zIndex: 10001 }
                                                }}
                                                sx={{
                                                    color: 'var(--text-primary)',
                                                    backgroundColor: 'var(--bg-secondary)',
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: 'var(--border-color)',
                                                    },
                                                    '& .MuiSvgIcon-root': {
                                                        color: 'var(--text-secondary)',
                                                    },
                                                }}
                                            >
                                                {tokenMonobankAccounts.map((acc, index) => (
                                                    <MenuItem key={index} value={acc.id}>
                                                        {acc.title}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Кнопки */}
                    <div className="requisite-form-actions">
                        <button className="cancel" onClick={onClose}>{t('forms:requisiteForm.cancelBtn')}</button>
                        <button className="confirm" onClick={handleSubmit} disabled={submitting || showBankWarning}>
                            {submitting ? t('common:sending') : (isEditing ? t('forms:requisiteForm.saveChanges') : t('forms:requisiteForm.addBtn'))}
                        </button>
                    </div>
                </>
            )}
        </Modal>

        {/* Модалка предупреждения о нераспарсенном банке */}
        <Modal
            isOpen={showBankWarning}
            onRequestClose={() => {}}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc={false}
            className="requisite-form-modal bank-warning-modal"
            overlayClassName="requisite-form-overlay bank-warning-overlay"
        >
            <div className="bank-warning-content">
                <div className="bank-warning-icon">⚠️</div>
                <h2 className="bank-warning-title">{t('forms:requisiteForm.bankWarningTitle', 'Важное уведомление')}</h2>
                <p className="bank-warning-text">{bankWarningMessage}</p>
                <button
                    className="bank-warning-btn"
                    onClick={() => {
                        setShowBankWarning(false);
                        setBankWarningMessage('');
                        onSuccess();
                    }}
                >
                    {t('forms:requisiteForm.bankWarningBtn', 'ПРОЧИТАНО')}
                </button>
            </div>
        </Modal>
        </>
    );
}
